import { isPlatformBrowser } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable, Optional, PLATFORM_ID } from '@angular/core';

import { EMPTY, of } from 'rxjs';

import { WsChannelRef } from './ws-channel';
import { WEBSOCKET_CONFIG } from './ws.config';
import { WebSocketConfig, WS_STATUS, WsChannel } from './ws.interfaces';
import { SentryLoggerService } from '../services/error-handler/sentry-logger.service';


@Injectable({
  providedIn: 'root'
})
export class WebSocketService {
  private readonly _url: string;
  private readonly _reconnectInterval: number;
  private readonly _reconnectAttempts: number;
  private readonly _verbose: boolean;
  private readonly _isBrowser: boolean;

  // The config is optional because not every app has realtime: web-marketplace hosts chat over
  // REST alone. Without it a feature degrades to its own fallback, instead of a NullInjectorError
  // taking down the screen that hosts it.
  constructor(@Optional() private _sentryLogger: SentryLoggerService,
              @Optional() @Inject(WEBSOCKET_CONFIG) wsConfig: WebSocketConfig,
              @Inject(PLATFORM_ID) platformId: Object) {

    this._url = wsConfig?.url;
    this._reconnectInterval = wsConfig?.reconnectInterval || 1000;
    this._reconnectAttempts = wsConfig?.reconnectAttempts || 10;
    this._verbose = wsConfig?.verbose || false;
    this._isBrowser = isPlatformBrowser(platformId);
  }

  /** The caller owns what it gets back and is responsible for closing it. */
  public connect<TIn, TOut>(path: string): WsChannel<TIn, TOut> {
    if (!this._isBrowser) {
      return this._inertChannel<TIn, TOut>();
    }

    if (!this._url) {
      this._log('No url configured, realtime is off for', path);

      return this._inertChannel<TIn, TOut>();
    }

    this._log('Opening', path);

    return new WsChannelRef<TIn, TOut>(`${this._url}/${path}`, {
      reconnectInterval: this._reconnectInterval,
      reconnectAttempts: this._reconnectAttempts,
      log: (...args) => this._log(...args),
      onError: (error) => this._handleError(error),
    });
  }

  private _inertChannel<TIn, TOut>(): WsChannel<TIn, TOut> {
    return {
      messages$: EMPTY,
      // IDLE, not FAILED: there is no socket on the server and that is not a fault.
      status$: of(WS_STATUS.IDLE),
      send: () => false,
      close: () => undefined
    };
  }

  // Socket failures are `CloseEvent`s, not HTTP responses, but `httpError` is the only reporting
  // entry point the logger exposes. The cast is the lie made visible rather than hidden by an
  // untyped parameter.
  private _handleError(error: unknown) {
    this._sentryLogger?.httpError(error as HttpErrorResponse);
  }

  private _log(...args: unknown[]) {
    if (!this._verbose) {
      return;
    }

    console.log('[WS]', ...args);
  }
}
